import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  ManufacturerModelDetail,
  ManufacturerModelForm,
  ManufacturerModelLicensingWeightFilter,
  ManufacturerModelListMeta,
  ManufacturerModelManufacturerFilter,
  ManufacturerModelMasterModelFilter,
  ManufacturerModelRow,
  ManufacturerModelYearFilter,
  ModelFormDropDownKey,
  ModelFormDropDownOption,
} from "../types";
import {
  initialFormDropDownState,
  initialManufacturerModelForm,
  initialState,
} from "./initialState";

export const sliceKey = "manufacturerModelState";

type ManufacturerModelListSuccessPayload = {
  data: ManufacturerModelRow[];
  meta: ManufacturerModelListMeta;
};

type FormDropDownSuccessPayload = {
  key: ModelFormDropDownKey;
  data: ModelFormDropDownOption[];
  meta: ManufacturerModelListMeta;
};

const manufacturerModelSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getManufacturerModelsList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getModelsSuccess(state, action: PayloadAction<ManufacturerModelListSuccessPayload>) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getManufacturerModelsListFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(state, action: PayloadAction<ManufacturerModelListSuccessPayload>) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<ManufacturerModelManufacturerFilter>
    ) {
      state.manufacturerFilter = action.payload;
    },

    setMasterModelFilter(
      state,
      action: PayloadAction<ManufacturerModelMasterModelFilter>
    ) {
      state.masterModelFilter = action.payload;
    },


    setWeightFilter(
      state,
      action: PayloadAction<ManufacturerModelLicensingWeightFilter>
    ) {
      state.licensingWeightFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.form = initialManufacturerModelForm;
      state.modelFormDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        superModels: { ...initialFormDropDownState },
      };
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.form = initialManufacturerModelForm;
      state.modelFormDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        superModels: { ...initialFormDropDownState },
      };
    },

    getFormDropDownList(state, action: PayloadAction<ModelFormDropDownKey>) {
      const key = action.payload;
      state.modelFormDropDowns[key] = {
        ...initialFormDropDownState,
        perPage: state.modelFormDropDowns[key].perPage,
        loading: true,
      };
    },

    getFormDropDownListSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>
    ) {
      const { key, data, meta } = action.payload;
      state.modelFormDropDowns[key] = {
        ...state.modelFormDropDowns[key],
        items: data,
        meta,
        page: meta.currentPage,
        loading: false,
      };
    },

    getFormDropDownListError(
      state,
      action: PayloadAction<{ key: ModelFormDropDownKey; message: string }>
    ) {
      state.modelFormDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    getFormDropDownNextPage(state, action: PayloadAction<ModelFormDropDownKey>) {
      const dropDown = state.modelFormDropDowns[action.payload];
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getFormDropDownNextPageSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>
    ) {
      const { key, data, meta } = action.payload;
      const dropDown = state.modelFormDropDowns[key];
      dropDown.loading = false;
      dropDown.items = dropDown.items.concat(data);
      dropDown.meta = meta;
      dropDown.page = meta.currentPage;
    },

    getFormDropDownNextPageError(
      state,
      action: PayloadAction<{ key: ModelFormDropDownKey; message: string }>
    ) {
      state.modelFormDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof ManufacturerModelForm;
        value: string | boolean;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value as never;
    },

    resetForm(state) {
      state.form = initialManufacturerModelForm;
    },

    addManufacturerModel(state, action: PayloadAction<ManufacturerModelForm>) {
      state.loading = true;
    },

    createManufacturerModelSuccess(state, action: PayloadAction<ManufacturerModelRow>) {
      state.loading = false;
      state.list = placeCreatedRowFirst(state.list, action.payload);
      state.form = initialManufacturerModelForm;
    },

    createManufacturerModelFailure(state) {
      state.loading = false;
    },

    setSelectedId(state, action: PayloadAction<number>) {
      state.selectedId = action.payload;
    },

    getModelDetails(state, action: PayloadAction<number>) {
      state.loading = true;
      state.selectedId = action.payload;
    },
    getModelDetailsSuccess(state, action: PayloadAction<ManufacturerModelDetail>) {
      state.loading = false;
      const detail = action.payload;

      state.form = {
        ...initialManufacturerModelForm,
        manufacturerID: detail.manufacturerName || String(detail.manufacturerID),
        year: String(detail.year),
        modelDesc: detail.modelDesc || "",
        chassisWidth: String(detail.chassisWidth),
        chassisHeight: String(detail.chassisHeight),
        baseBeamType: detail.baseBeamType || "",
        maxWidth: String(detail.maxWidth),
        frontExtension: String(detail.frontExtension),
        licenseWeight: String(detail.licenseWeight),
        weelDrive: detail.weelDrive || "",
        baseBeamTypeRoof: detail.baseBeamTypeRoof || "",
        chassisWeight: String(detail.chassisWeight),
        frontWeelBase: String(detail.frontWeelBase),
        registrationInstruction: detail.registrationOrderNumber || "",
        notes: detail.remarks || "",
        sideLighting: detail.teken95SideLight === "Y" || detail.sideLighting === true,
        sideGuard: detail.teken95SideShld === "Y" || detail.sideGuard === true,
        mudguard: detail.teken95MadShld === "Y" || detail.mudguard === true,
        extendableChassis: detail.extendableChassis === true,
      };
    },
    getModelDetailsFailure(state) {
      state.loading = false;
    },

    updateModel(state, action: PayloadAction<{ id: number; form: ManufacturerModelForm }>) {
      state.loading = true;
      state.selectedId = action.payload.id;
    },
    updateModelSuccess(state, action: PayloadAction<ManufacturerModelRow>) {
      state.loading = false;
      const idx = state.list.findIndex(model=> model.manufacturerModelID === action.payload.manufacturerModelID);
      if (idx !== -1) state.list[idx] = action.payload;
    },
    updateModelFailure(state) {
      state.loading = false;
    },

    deleteModel(state, action: PayloadAction<number>) {
      state.loading = true;
      state.selectedId = action.payload;
    },
    deleteModelSuccess(state, action: PayloadAction<number>) {
      state.loading = false;
      state.list = state.list.filter(m => m.manufacturerModelID !== action.payload);
      state.selectedId = 0;
    },
    deleteModelFailure(state) {
      state.loading = false;
    },

    getFilterOptions(state) {
      state.loading = true;
    },




    getManufacturerOptionsSuccess(state, action: PayloadAction<{ id: number; name: string }[]>) {
      state.loading = false;
      state.manufacturerOptions = action.payload;
      state.modelFormDropDowns.manufacturers.items = action.payload;
    },
    getSuperModelsOptionsSuccess(state, action: PayloadAction<{ id: number; name: string }[]>) {
      state.loading = false;
      state.masterModelOptions = action.payload;
      state.modelFormDropDowns.superModels.items = action.payload;
    },
    getweightOptionsSuccess(state, action: PayloadAction<number[]>) {
      state.loading = false;
      state.WeightFilterOptions = action.payload;
   },





    
    setYearFilter(state, action: PayloadAction<ManufacturerModelYearFilter>) {
      state.yearFilter = action.payload;
    },
    getYearsOptionsSuccess(state, action: PayloadAction<number[]>) {
      state.loading = false;
      state.yearFilterOptions = action.payload;
    },
    getFilterOptionsFailure(state) {
      state.loading = false;
    },
  },
});

export const { actions: manufacturerModelActions, reducer } =
  manufacturerModelSlice;